<template>
  <div class="conditional-format-editor">
    <b-field class="">
      <b-switch
        v-model="enabled"
        type="is-info">
        Enable conditional formatting
      </b-switch>
    </b-field>

    <div
      v-if="inputs"
      class="sub-section mt-6">
      <h3>Conditional Formatting</h3>

      <b-field
        v-if="!hideDirection"
        class="input-field"
        label="When measure exceeds benchmark, show as:">
        <singleselect
          v-model="inputs.is_greater_better"
          :append-to-body="true"
          :options="isGreaterBetterOptions"
          :return-as-object="false"
          :enable-search="false"
          label="label"
          track-by="key"
          header-placeholder="Select color" />
      </b-field>

      <!-- Whoever hid the direction control owns saying where direction
           comes from instead. -->
      <slot name="direction" />

      <b-field
        class="input-field"
        label="Compare thresholds to the benchmark as:">
        <singleselect
          v-model="comparisonTypeSelection"
          :append-to-body="true"
          :options="comparisonTypeOptions"
          :return-as-object="false"
          :enable-search="false"
          label="label"
          track-by="key"
          header-placeholder="Select comparison type" />
      </b-field>

      <div class="threshold-unit mb-4">
        <span class="type-label">Thresholds read as:</span>
        <b-tag type="is-info is-light">
          {{ thresholdUnit.tag }}
        </b-tag>
        <span
          v-if="isInheritingComparisonType"
          class="type-label has-text-grey">
          {{ inheritedComparisonHelp }}
        </span>
      </div>

      <h4 class="mt-5 mb-1">
        Upper Threshold
      </h4>
      <b-field
        class="input-field"
        :label="upperThresholdLabel">
        <b-numberinput
          v-model="upperThreshold"
          step="any"
          controls-alignment="right"
          expanded />
      </b-field>

      <h4 class="mt-5 mb-1">
        Lower Threshold
      </h4>

      <b-field
        class="input-field"
        :label="lowerThresholdLabel">
        <b-numberinput
          v-model="lowerThreshold"
          step="any"
          controls-alignment="right"
          expanded />
      </b-field>
    </div>
  </div>
</template>

<script>
  import singleselect from '@/components/ui/singleselect.vue'

  export default {
    name: 'ConditionalFormatEditor',
    components: {
      singleselect,
    },
    props: {
      // The conditional_format object, or null for "no formatting". Null is a
      // meaningful value here, not an absent one, so this takes the prop
      // directly rather than through ModuleConfigMixin, which fills a null in.
      value: {
        type: Object,
        default: null,
      },
      // What the line being coloured displays. Decides what an unqualified
      // threshold is measured in, so it has to come from whoever owns it —
      // a row keeps it in comparison_config, a benchmark on itself.
      differenceType: {
        type: String,
        default: 'none',
      },
      // `parentMeasure` and `parentIsPctTotal`: the units a raw value is
      // stored in, which is what an absolute threshold is measured against.
      context: {
        type: Object,
        default: () => ({}),
      },
      hideDirection: {
        type: Boolean,
        default: false,
      },
      // What the "match the displayed value" option resolves to, for callers
      // whose difference type is not fixed at config time.
      inheritedComparisonHelp: {
        type: String,
        default: 'matching how this row is displayed',
      },
    },
    data () {
      return {
        isGreaterBetterOptions: [
          {
            key: true,
            label: 'Green',
          },
          {
            key: false,
            label: 'Red',
          },
        ],

        comparisonTypeOptions: [
          {
            key: 'inherit',
            label: 'Match the displayed value',
          },
          {
            key: 'diff',
            label: 'Difference',
          },
          {
            key: 'var',
            label: 'Percentage change',
          },
        ],
      }
    },
    computed: {
      inputs: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },
      enabled: {
        get () {
          return this.inputs !== null && this.inputs !== undefined
        },
        set (enabled) {
          this.inputs = enabled
            ? {
              'is_greater_better': true,
              'comparison_type': null,
              'lower_threshold': 0,
              'upper_threshold': 0,
            }
            : null
        },
      },
      comparisonTypeSelection: {
        get () {
          return this.inputs?.comparison_type || 'inherit'
        },
        set (comparisonType) {
          this.$set(
            this.inputs,
            'comparison_type',
            comparisonType === 'inherit' ? null : comparisonType
          )
        },
      },
      isInheritingComparisonType () {
        return !this.inputs?.comparison_type
      },
      // Mirrors resolve_comparison_type on the backend: an explicit type wins,
      // otherwise the thresholds are read in whatever terms the line displays.
      // A difference type the measure decides falls back to `diff`, which is
      // the same fallback the renderer takes.
      effectiveComparisonType () {
        const explicit = this.inputs?.comparison_type
        if (explicit) return explicit
        return this.differenceType === 'var' ? 'var' : 'diff'
      },
      parentMeasureDataType () {
        if (this.context.parentIsPctTotal) return 'percentage'
        const measureKey = this.context.parentMeasure?.key
        if (!measureKey) return null
        const options = this.$store.getters['app/reportsConfig/companyMeasureOptions']
        return options?.[measureKey]?.data_type || null
      },
      // How a stored threshold is read and displayed. `decimalShift` is the
      // power of ten from the stored value to the one shown: money is stored
      // in cents and percentages as fractions, so a bare number input would
      // otherwise read 100 as a dollar and 5 as 500%.
      // The inputs take step="any" rather than a per-unit step: a step is a
      // validation rule as well as an arrow increment, and 0.5 rejects 4.6.
      thresholdUnit () {
        if (this.effectiveComparisonType === 'var') {
          return {
            noun: 'change',
            suffix: '%',
            tag: 'Percentage change (%)',
            decimalShift: 2,
          }
        }
        if (this.parentMeasureDataType === 'money') {
          return {
            noun: 'difference',
            suffix: '$',
            tag: 'Dollar difference ($)',
            decimalShift: -2,
          }
        }
        if (this.parentMeasureDataType === 'percentage') {
          return {
            noun: 'difference',
            suffix: 'percentage points',
            tag: 'Difference in percentage points',
            decimalShift: 2,
          }
        }
        return {
          noun: 'difference',
          suffix: null,
          tag: 'Difference in measure units',
          decimalShift: 0,
        }
      },
      upperThresholdLabel () {
        return this.thresholdLabel('exceeds')
      },
      lowerThresholdLabel () {
        return this.thresholdLabel('is below')
      },
      upperThreshold: {
        get () {
          return this.toDisplayThreshold(this.inputs?.upper_threshold)
        },
        set (threshold) {
          this.setThreshold('upper_threshold', threshold)
        },
      },
      lowerThreshold: {
        get () {
          return this.toDisplayThreshold(this.inputs?.lower_threshold)
        },
        set (threshold) {
          this.setThreshold('lower_threshold', threshold)
        },
      },
    },
    methods: {
      thresholdLabel (verb) {
        const { noun, suffix, } = this.thresholdUnit
        if (!suffix) return `Apply formatting when the ${noun} ${verb}:`
        return `Apply formatting when the ${noun} ${verb} (${suffix}):`
      },
      // toPrecision keeps the shift from surfacing float noise, so 0.05 reads
      // as 5 rather than 5.000000000000001.
      shiftDecimal (threshold, decimalShift) {
        if ([ null, undefined, '', ].includes(threshold)) return threshold
        if (decimalShift === 0) return threshold
        return Number((threshold * Math.pow(10, decimalShift)).toPrecision(12))
      },
      toDisplayThreshold (threshold) {
        return this.shiftDecimal(threshold ?? 0, this.thresholdUnit.decimalShift)
      },
      setThreshold (field, threshold) {
        this.$set(
          this.inputs,
          field,
          this.shiftDecimal(threshold ?? 0, -this.thresholdUnit.decimalShift)
        )
      },
    },
  }
</script>

<style lang="scss" scoped>
.threshold-unit {
  display: flex;
  align-items: center;
  gap: 8px;

  .type-label {
    font-size: 14px;
    font-weight: 500;
    color: #555;
  }
}
</style>
